import { format, parseISO } from 'date-fns'
import { Facebook, Instagram, MousePointerClick, Reply, Target, TrendingUp } from 'lucide-react'
import { ReportChart } from '@/components/relatorios/ReportChart'
import { ReportKpiCard } from '@/components/relatorios/ReportKpiCard'
import { ReportShell } from '@/components/relatorios/ReportShell'
import { ReportTable } from '@/components/relatorios/ReportTable'
import { exportCsv, formatCurrency, formatDuration, formatPercent } from '@/components/relatorios/reportUtils'
import { useMarketingPerformanceReport } from '@/hooks/reports/reportHooks'
import { useReportDateRange } from '@/hooks/reports/useReportDateRange'

const funnelPalette = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)']

const heroAccents = [
  { bg: 'bg-blue-50 dark:bg-blue-500/15', fg: 'text-[#2563EB]', value: 'text-[#2563EB]', Icon: MousePointerClick },
  { bg: 'bg-green-50 dark:bg-green-500/15', fg: 'text-[#22C55E]', value: 'text-[#22C55E]', Icon: Reply },
  { bg: 'bg-purple-50 dark:bg-purple-500/15', fg: 'text-[#9333EA]', value: 'text-[#9333EA]', Icon: Target },
  { bg: 'bg-orange-50 dark:bg-orange-500/15', fg: 'text-[#F97316]', value: 'text-[#F97316]', Icon: TrendingUp },
] as const

function HeroStat({
  index,
  label,
  value,
  helper,
}: {
  index: number
  label: string
  value: string
  helper?: string
}) {
  const accent = heroAccents[index % heroAccents.length]
  const Icon = accent.Icon
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent.bg} ${accent.fg}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={`text-2xl font-semibold ${accent.value}`}>{value}</p>
        </div>
      </div>
      {helper && <p className="mt-3 text-xs text-muted-foreground">{helper}</p>}
    </div>
  )
}

export default function PerformanceMarketingReport() {
  const [range, setRange] = useReportDateRange()
  const report = useMarketingPerformanceReport(range)
  const { totals, funnel, daily, byPlatform, creatives, formBreakdown } = report.data

  const funnelSteps = [
    { label: 'Leads de anúncio', value: funnel.leads },
    { label: 'Atendidos', value: funnel.responded },
    { label: 'Oportunidades', value: funnel.opportunities },
    { label: 'Vendas', value: funnel.sales },
  ]
  const funnelMax = funnelSteps[0].value || 1

  return (
    <ReportShell
      title="Performance de Marketing"
      description="Leads que chegaram por anúncios (Click to WhatsApp) até a venda: atribuição, atendimento e receita."
      {...range}
      onRangeChange={setRange}
      loading={report.loading}
      error={report.error}
      empty={totals.leads === 0}
      onRetry={report.refetch}
      onExport={() =>
        exportCsv('performance-marketing', creatives, [
          { label: 'Criativo', value: (row) => row.title },
          { label: 'Ad ID', value: (row) => (row.attributionKey.includes(':') ? row.attributionKey.split(':')[1] : 'Sem ID de anúncio') },
          { label: 'Plataforma', value: (row) => row.sourceApp },
          { label: 'Leads', value: (row) => row.leads },
          { label: 'Atendidos', value: (row) => row.responded },
          { label: 'Oportunidades', value: (row) => row.opportunities },
          { label: 'Vendas', value: (row) => row.sales },
          { label: 'Receita', value: (row) => row.revenue.toFixed(2).replace('.', ',') },
          { label: 'Conversão (%)', value: (row) => row.conversion.toFixed(1).replace('.', ',') },
          { label: 'Ticket médio', value: (row) => row.averageTicket.toFixed(2).replace('.', ',') },
        ])
      }
    >
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <HeroStat index={0} label="Leads de anúncio" value={totals.leads.toLocaleString('pt-BR')} helper="Contatos que iniciaram via anúncio no período" />
        <HeroStat index={1} label="Receita atribuída" value={formatCurrency(totals.revenue)} helper={`${totals.sales} venda(s) fechada(s)`} />
        <HeroStat index={2} label="Conversão lead → venda" value={formatPercent(totals.leadToSale)} helper={`${totals.opportunities} oportunidade(s) geradas`} />
        <HeroStat index={3} label="1ª resposta média" value={formatDuration(totals.averageFirstResponseSeconds)} helper={`${totals.responded} lead(s) atendidos`} />
      </section>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiCard label="Oportunidades" value={totals.opportunities.toLocaleString('pt-BR')} />
        <ReportKpiCard label="Vendas" value={totals.sales.toLocaleString('pt-BR')} />
        <ReportKpiCard label="Leads via Facebook" value={totals.facebook.toLocaleString('pt-BR')} icon={<Facebook className="h-4 w-4" />} />
        <ReportKpiCard label="Leads via Instagram" value={totals.instagram.toLocaleString('pt-BR')} icon={<Instagram className="h-4 w-4" />} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold text-foreground">Funil de conversão</h2>
          <p className="mt-1 text-xs text-muted-foreground">Da chegada do lead até a venda.</p>
          <div className="mt-4 flex flex-col gap-3">
            {funnelSteps.map((step, index) => {
              const width = Math.max((step.value / funnelMax) * 100, step.value > 0 ? 6 : 2)
              const previous = index === 0 ? step.value : funnelSteps[index - 1].value
              const stepRate = previous ? (step.value / previous) * 100 : 0
              return (
                <div key={step.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{step.label}</span>
                    <span className="tabular-nums text-muted-foreground">
                      {step.value.toLocaleString('pt-BR')}
                      {index > 0 && <span className="ml-2 text-xs">({formatPercent(stepRate)})</span>}
                    </span>
                  </div>
                  <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${width}%`, backgroundColor: funnelPalette[index % funnelPalette.length] }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <ReportChart
          type="line"
          data={daily.map((row) => ({ ...row, label: format(parseISO(row.date), 'dd/MM') }))}
          xKey="label"
          series={[
            { key: 'leads', label: 'Leads', color: 'var(--chart-1)' },
            { key: 'sales', label: 'Vendas', color: 'var(--chart-2)' },
          ]}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">Desempenho por plataforma</h2>
          <ReportChart
            type="bar"
            data={byPlatform}
            xKey="label"
            series={[
              { key: 'leads', label: 'Leads', color: 'var(--chart-1)' },
              { key: 'sales', label: 'Vendas', color: 'var(--chart-2)' },
            ]}
          />
        </div>
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">Plataformas</h2>
          <ReportTable
            rows={byPlatform}
            rowKey={(row) => row.platform || 'none'}
            columns={[
              { key: 'label', label: 'Plataforma', render: (row) => <span className="font-medium text-foreground">{row.label}</span> },
              { key: 'leads', label: 'Leads', align: 'right', render: (row) => row.leads },
              { key: 'sales', label: 'Vendas', align: 'right', render: (row) => row.sales },
              { key: 'revenue', label: 'Receita', align: 'right', render: (row) => formatCurrency(row.revenue) },
              { key: 'conversion', label: 'Conv.', align: 'right', render: (row) => formatPercent(row.conversion) },
            ]}
          />
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-foreground">Ranking de criativos (Ad ID)</h2>
        <ReportTable
          rows={creatives}
          rowKey={(row) => row.attributionKey}
          columns={[
            { key: 'title', label: 'Criativo', render: (row) => <span className="font-medium text-foreground">{row.title}</span> },
            {
              key: 'adId',
              label: 'Ad ID',
              render: (row) => {
                const id = row.attributionKey.includes(':')
                  ? row.attributionKey.split(':')[1]
                  : null
                return id ? <span className="font-mono text-xs text-muted-foreground">{id}</span> : <span className="text-xs text-muted-foreground">Sem ID de anúncio</span>
              },
            },
            { key: 'sourceApp', label: 'Plataforma', render: (row) => <span className="capitalize">{row.sourceApp}</span> },
            { key: 'leads', label: 'Leads', align: 'right', render: (row) => row.leads },
            { key: 'responded', label: 'Atendidos', align: 'right', render: (row) => row.responded },
            { key: 'opportunities', label: 'Oport.', align: 'right', render: (row) => row.opportunities },
            { key: 'sales', label: 'Vendas', align: 'right', render: (row) => row.sales },
            { key: 'revenue', label: 'Receita', align: 'right', render: (row) => formatCurrency(row.revenue) },
            { key: 'conversion', label: 'Conversão', align: 'right', render: (row) => formatPercent(row.conversion) },
            { key: 'averageTicket', label: 'Ticket médio', align: 'right', render: (row) => formatCurrency(row.averageTicket) },
          ]}
        />
      </div>

      {formBreakdown.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold text-foreground">Respostas do formulário</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Quais perfis de lead mais convertem em venda.
          </p>
          <ReportTable
            rows={formBreakdown}
            rowKey={(row, index) => `${row.question}-${row.answer}-${index}`}
            columns={[
              { key: 'question', label: 'Pergunta', render: (row) => row.question },
              { key: 'answer', label: 'Resposta', render: (row) => <span className="font-medium text-foreground">{row.answer}</span> },
              { key: 'leads', label: 'Leads', align: 'right', render: (row) => row.leads },
              { key: 'sales', label: 'Vendas', align: 'right', render: (row) => row.sales },
              { key: 'revenue', label: 'Receita', align: 'right', render: (row) => formatCurrency(row.revenue) },
              { key: 'conversion', label: 'Conversão', align: 'right', render: (row) => formatPercent(row.conversion) },
            ]}
          />
        </div>
      )}
    </ReportShell>
  )
}
